import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, User, LogIn, LogOut, Calendar, Edit2, Save, X, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../services/api';

const Attendance = () => {
    const [status, setStatus] = useState({ checkedIn: false, isOnBreak: false, sessions: [], checkIn: null, checkOut: null });
    const [history, setHistory] = useState([]);
    const [view, setView] = useState('today'); // 'today' or 'history'
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [comment, setComment] = useState('');
    const [editingSessionId, setEditingSessionId] = useState(null);
    const [editComment, setEditComment] = useState('');
    const [historyMonth, setHistoryMonth] = useState(new Date().getMonth() + 1);
    const [historyYear, setHistoryYear] = useState(new Date().getFullYear());

    const user = JSON.parse(localStorage.getItem('user'));

    useEffect(() => {
        fetchStatus();
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (view === 'history') {
            fetchHistory();
        }
    }, [view, historyMonth, historyYear]);

    const fetchStatus = async () => {
        try {
            const res = await api.get(`/attendance/status/${user.id}`);
            setStatus(res.data);
        } catch (error) {
            console.error('Failed to fetch attendance status');
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async () => {
        try {
            const res = await api.get(`/attendance/history/${user.id}?month=${historyMonth}&year=${historyYear}`);
            setHistory(res.data);
        } catch (error) {
            console.error('Failed to fetch attendance history');
        }
    };

    const handleCheckIn = async () => {
        try {
            await api.post('/attendance/check-in', { userId: user.id });
            fetchStatus();
            setMessage({ type: 'success', text: 'Checked in successfully!' });
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to check in' });
        }
    };

    const handleCheckOut = async () => {
        try {
            await api.post('/attendance/check-out', { userId: user.id, comments: comment });
            setComment('');
            fetchStatus();
            setMessage({ type: 'success', text: 'Checked out successfully!' });
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to check out' });
        }
    };

    const handleUpdateSession = async (sessionId) => {
        try {
            await api.put('/attendance/session', { sessionId, comments: editComment });
            setEditingSessionId(null);
            setEditComment('');
            fetchStatus(); // Refresh to show updated comment
            if (view === 'history') fetchHistory();
            setMessage({ type: 'success', text: 'Comment updated successfully!' });
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update comment' });
        }
    };

    const formatTime = (date) => {
        if (!date) return '--:--';
        return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString([], { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
    };

    const calculateTotalDuration = (sessions) => {
        let totalMs = 0;
        sessions.forEach(s => {
            const start = new Date(s.checkIn);
            const end = s.checkOut ? new Date(s.checkOut) : new Date();
            totalMs += (end - start);
        });

        const totalMinutes = Math.floor(totalMs / 60000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        return `${hours}h ${minutes}m`;
    };

    if (loading) return <div className="container section">Loading...</div>;

    return (
        <div className="container section">
            <div className="glass-card" style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                    <div className="btn-group">
                        <button
                            className={`btn ${view === 'today' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setView('today')}
                        >
                            Today
                        </button>
                        <button
                            className={`btn ${view === 'history' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setView('history')}
                        >
                            History
                        </button>
                    </div>
                </div>

                {view === 'today' && (
                    <>
                        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                            <div style={{
                                width: '80px', height: '80px', borderRadius: '50%', backgroundColor: status.checkedIn ? '#dcfce7' : '#eff6ff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem',
                                color: status.checkedIn ? '#059669' : '#2563eb',
                                animation: status.checkedIn ? 'pulse 2s infinite' : 'none'
                            }}>
                                <Clock size={40} />
                            </div>
                            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>Daily Attendance</h1>
                            <p style={{ color: '#6b7280' }}>
                                {status.checkedIn ? 'You are currently working' : status.isOnBreak ? 'You are on a break' : 'Mark your presence'}
                            </p>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
                            <div style={{ textAlign: 'center', padding: '1rem', background: '#f8fafc', borderRadius: '0.75rem' }}>
                                <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Time</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1e293b', fontFamily: 'monospace' }}>
                                    {currentTime.toLocaleTimeString()}
                                </div>
                            </div>
                            <div style={{ textAlign: 'center', padding: '1rem', background: '#f0fdf4', borderRadius: '0.75rem' }}>
                                <div style={{ fontSize: '0.75rem', color: '#166534', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Hours</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#15803d', fontFamily: 'monospace' }}>
                                    {calculateTotalDuration(status.sessions || [])}
                                </div>
                            </div>
                        </div>

                        {message && (
                            <div style={{
                                padding: '1rem', borderRadius: '0.5rem', marginBottom: '1.5rem', textAlign: 'center',
                                backgroundColor: message.type === 'success' ? '#dcfce7' : '#fee2e2',
                                color: message.type === 'success' ? '#166534' : '#991b1b'
                            }}>
                                {message.text}
                            </div>
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2.5rem' }}>
                            {!status.checkedIn ? (
                                <button onClick={handleCheckIn} className="btn btn-primary" style={{ height: '4rem', fontSize: '1.25rem', gap: '0.75rem', justifyContent: 'center', display: 'flex', alignItems: 'center' }}>
                                    <LogIn size={24} /> {status.sessions?.length > 0 ? 'Resume Work' : 'Check In'}
                                </button>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <textarea
                                        placeholder="Add a comment before checking out (optional)..."
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #cbd5e1', fontSize: '0.875rem' }}
                                    />
                                    <button onClick={handleCheckOut} className="btn btn-secondary" style={{ height: '4rem', fontSize: '1.25rem', backgroundColor: '#dc2626', color: 'white', border: 'none', gap: '0.75rem', justifyContent: 'center', display: 'flex', alignItems: 'center' }}>
                                        <LogOut size={24} /> Pause / Check Out
                                    </button>
                                </div>
                            )}
                        </div>

                        {status.sessions?.length > 0 && (
                            <div style={{ marginTop: '1rem' }}>
                                <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#475569', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Calendar size={16} /> Today's Sessions
                                </h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    {status.sessions.map((session, index) => (
                                        <div key={session.id} style={{
                                            padding: '0.75rem 1rem', background: '#f1f5f9', borderRadius: '0.5rem',
                                            borderLeft: `4px solid ${session.checkOut ? '#cbd5e1' : '#22c55e'}`
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                                <div>
                                                    <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>Session {index + 1}</div>
                                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                                        {formatTime(session.checkIn)} - {session.checkOut ? formatTime(session.checkOut) : 'Active'}
                                                    </div>
                                                </div>
                                                {session.checkOut && (
                                                    <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#475569' }}>
                                                        {Math.floor((new Date(session.checkOut) - new Date(session.checkIn)) / 60000)} mins
                                                    </div>
                                                )}
                                            </div>

                                            {editingSessionId === session.id ? (
                                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                    <input
                                                        type="text"
                                                        value={editComment}
                                                        onChange={(e) => setEditComment(e.target.value)}
                                                        className="form-input"
                                                        style={{ flex: 1, padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                                                    />
                                                    <button onClick={() => handleUpdateSession(session.id)} className="btn btn-icon btn-sm text-green-600"><Save size={16} /></button>
                                                    <button onClick={() => setEditingSessionId(null)} className="btn btn-icon btn-sm text-red-600"><X size={16} /></button>
                                                </div>
                                            ) : (
                                                <div style={{ fontSize: '0.875rem', color: '#64748b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <span>{session.comments || <em style={{ opacity: 0.5 }}>No comments</em>}</span>
                                                    <button onClick={() => { setEditingSessionId(session.id); setEditComment(session.comments || ''); }} className="btn btn-icon btn-sm text-blue-600" title="Edit Comment">
                                                        <Edit2 size={14} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {view === 'history' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <button onClick={() => setHistoryMonth(m => m === 1 ? 12 : m - 1)} className="btn btn-icon"><ChevronLeft /></button>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{new Date(historyYear, historyMonth - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}</h2>
                            <button onClick={() => setHistoryMonth(m => m === 12 ? 1 : m + 1)} className="btn btn-icon"><ChevronRight /></button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {history.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No attendance records found for this month.</div>
                            ) : (
                                history.map(record => (
                                    <div key={record.id} style={{ border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.5rem' }}>
                                            <span style={{ fontWeight: 'bold' }}>{formatDate(record.date)}</span>
                                            <span style={{ fontWeight: 'bold', color: '#15803d' }}>{calculateTotalDuration(record.sessions || [])}</span>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            {record.sessions.map((session, idx) => (
                                                <div key={session.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', color: '#475569' }}>
                                                    <span>Session {idx + 1}: {formatTime(session.checkIn)} - {formatTime(session.checkOut)}</span>
                                                    <span>{session.comments}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
            <style>{`
                @keyframes pulse {
                    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4); }
                    70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(34, 197, 94, 0); }
                    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
                }
            `}</style>
        </div>
    );
};

export default Attendance;
